import Vue from 'vue'
import vuetify from '@/plugins/vuetify'
import { ExtendedVue } from 'vue/types/vue'
import ElementsFactory from '@/factory/ElementsFactory'
import { apiRoutes } from '@/helpers/ApiRoutes'
import { ActionInterface } from '@/config/interfaces'
import Utils from '@/helpers/Utils'

// IFrames for secureWebview aren't secure enough to handle payment
// Therefore, we redirect the user to our API, so he can continue paying securely
function resolveView (
  view: string,
  viewContent: Record<string, unknown> | string,
  postParams: URLSearchParams | null = null
): string {
  if (view === 'webview' && postParams !== null) {
    return 'postWebview'
  }
  if (
    typeof viewContent === 'string' &&
    (
      viewContent.includes(apiRoutes.topupEntry) ||
      viewContent.includes(apiRoutes.billEntry) ||
      viewContent.includes(apiRoutes.recurring) ||
      viewContent.includes(apiRoutes.paymentBillWalletEntry)
    )
  ) {
    return 'paymentView'
  }

  return view
}
function transformContent (viewContent: Record<string, unknown>): Record<string, unknown> {
  // eslint-disable-next-line
  const groups = viewContent.pages ? (viewContent.pages as any)[0].groups : viewContent.groups
  if (groups) {
    // eslint-disable-next-line
    groups.forEach((group: any) => {
      // eslint-disable-next-line
      group.items.forEach((item: any) => {
        // eslint-disable-next-line
        item.elements.forEach((element: any) => {
          if (element) {
            if ('uri' in element) {
              element.uri = element.uri.includes('silent-login') ? element.uri : decodeURIComponent(element.uri)
            }
          }
        })
      })
    })
  }
  return viewContent
}
// Global functions (must be typehinted in shims-vue.d.ts)
Vue.mixin({
  methods: {
    getView: function (
      view: string,
      viewContent: Record<string, unknown> | string,
      postParams: URLSearchParams | null = null,
      jumpOnCloseURI = ''
    ): void {
      if (typeof viewContent === 'string') {
        if (!viewContent.includes('silent-login')) {
          viewContent = decodeURIComponent(viewContent)
        }
      } else {
        viewContent = transformContent(viewContent)
      }
      // Dispatch post params if they exist
      if (postParams !== null) {
        this.$store.dispatch('postParams', postParams)
      }
      // T2-4511 Set (or reset) jumpOnCloseURI if passed via arg
      if (jumpOnCloseURI !== '') {
        this.$store.dispatch('commitJumpOnCloseURI', jumpOnCloseURI)
      }
      // First we check if we need to set left view as active
      if (view === 'left') {
        this.$router.push({ name: 'LeftView' }).catch(() => { /* Do nothing */ })
        this.$store.dispatch('toggleLeftViewState', true)
        // If the content is the same, we should call the API again
        this.$store.dispatch('commitAllowResend', this.$store.getters.getLeftViewContent === viewContent)
        this.$store.dispatch('commitLeftViewContent', viewContent)
      } else {
        // Now we need to resolve if view is card, webview, secureWebview...
        this.$store.dispatch('toggleViewState', resolveView(view, viewContent, postParams))
        // Same check as above, call the API again if the content matches
        this.$store.dispatch('commitAllowResend', this.$store.getters.getViewContent === viewContent)
        this.$store.dispatch('commitViewContent', viewContent)
        // Open card in left view if left view is already active
        if (this.$store.getters.getLeftViewState === true) {
          this.$router.push({ name: 'LeftCardView' }).catch(() => { /* Do nothing */ })
        } else {
          // Else, open any other view regularly
          this.$router.push({ name: 'CardView' }).catch(() => { /* Do nothing */ })
        }
      }
    },
    // eslint-disable-next-line
    getElement: function (elementName: string): ExtendedVue<any, any, any, any, any> {
      return ElementsFactory.getElementByName(elementName)
    },
    handleActionClick (action: ActionInterface): void {
      if (action.uri !== undefined) {
        this.getView(action.view, action.content || action.uri, this.getActionInputRefs(action), action.jumpOnCloseUri)
      }
    },
    getActionInputRefs (action: ActionInterface): URLSearchParams | null {
      if (!Object.prototype.hasOwnProperty.call(action, 'inputReferences')) {
        return null
      }
      const refs = action.inputReferences
      if (typeof refs !== 'undefined') {
        return Utils.getPostParamsByRefs(refs)
      }
      return null
    }
  },

  computed: {
    isDesktop (): boolean {
      return vuetify.framework.breakpoint.lgAndUp
    },
    isDarkMode (): boolean {
      return vuetify.framework.theme.dark
    }
  }
})
